import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { Inline, Radio, RadioGroup, Stack } from '@etg/wings';
import { useTranslation, useDateFormat } from '@eti/providers';
import { css } from '@eti/styles';
import { DoubleRangeSlider } from '@eti/components';
import Filter from './Filter';
import { formatDateTimeValue } from './utils/searchFilters';

const labelStyles = css`
  font-size: 0.875rem;
`;

const halfHour = 30 * 60 * 1000;
const startOfDay = 0;
const endOfDay = 24 * 60 * 60 * 1000;

const selectDefaultFilterType = (selectedFilters: any, index: number) => {
  if (selectedFilters[`TRIP_${index}_ARRIVAL_TIME`] !== null) {
    return 'arrival';
  }

  return 'departure';
};

const getActiveTimeRangeType = (selectedFilterType: string, index: number) =>
  selectedFilterType === 'departure'
    ? `TRIP_${index}_DEPARTURE_TIME`
    : `TRIP_${index}_ARRIVAL_TIME`;

const getInactiveTimeRangeType = (selectedFilterType: string, index: number) =>
  selectedFilterType === 'departure'
    ? `TRIP_${index}_ARRIVAL_TIME`
    : `TRIP_${index}_DEPARTURE_TIME`;

type DepartureArrivalTimeProps = {
  className: string;
  destinationName: string;
  index: number;
  isInitiallyExpanded: boolean;
  isToggleable: boolean;
  onFilter: (filter: any) => void;
  originName: string;
  selectedFilters: any;
};

const DepartureArrivalTime = ({
  className,
  destinationName,
  index,
  isInitiallyExpanded,
  isToggleable,
  onFilter,
  originName,
  selectedFilters,
}: DepartureArrivalTimeProps) => {
  const { t } = useTranslation();
  const { time: dateTime } = useDateFormat();
  const [selectedFilterType, setSelectedFilterType] = useState(
    selectDefaultFilterType(selectedFilters, index),
  );
  const activeTimeRangeType = getActiveTimeRangeType(selectedFilterType, index);
  const inactiveTimeRangeType = getInactiveTimeRangeType(selectedFilterType, index);
  const isSelected = selectedFilters[activeTimeRangeType] !== null;
  const initialSelectedValues = { min: startOfDay, max: endOfDay };
  const selectedValue = selectedFilters[activeTimeRangeType] || initialSelectedValues;

  const handleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (selectedFilters[activeTimeRangeType] !== null) {
      onFilter({
        [inactiveTimeRangeType]: { min: selectedValue.min, max: selectedValue.max },
        [activeTimeRangeType]: null,
      });
    }

    setSelectedFilterType(event.target.value);
  };

  const handleChange = (values: number[]) => {
    const [minSelected, maxSelected] = values;

    if (minSelected === startOfDay && maxSelected === endOfDay) {
      if (selectedFilters[activeTimeRangeType] !== null) {
        onFilter({ [activeTimeRangeType]: null });
      }
    } else {
      onFilter({
        [activeTimeRangeType]: { min: minSelected, max: maxSelected },
      });
    }
  };

  const handleReset = () => onFilter({ [activeTimeRangeType]: null });
  const { min: minKey, max: maxKey } =
    selectedFilterType === 'departure'
      ? {
          min: 'ScreenReader.Result.Filters.DepartureSlider.EarliestDeparture.Text',
          max: 'ScreenReader.Result.Filters.DepartureSlider.LatestDeparture.Text',
        }
      : {
          min: 'ScreenReader.Result.Filters.ArrivalSlider.EarliestArrival.Text',
          max: 'ScreenReader.Result.Filters.ArrivalSlider.LatestArrival.Text',
        };

  const fromToCities = t('ScreenReader.From.To.Text', originName, destinationName);

  const minValueLabelText = `${fromToCities}, ${t(minKey)}`;
  const maxValueLabelText = `${fromToCities}, ${t(maxKey)}`;

  return (
    <Filter
      className={className}
      destinationName={destinationName}
      isInitiallyExpanded={isInitiallyExpanded}
      isSelected={isSelected}
      isToggleable={isToggleable}
      name={`departureArrival${index}`}
      onReset={handleReset}
      originName={originName}
    >
      <Stack spacing={16}>
        <RadioGroup
          name={`departureArrivalTimeRange${index}`}
          onChange={handleSelect}
          selectedValue={selectedFilterType}
        >
          <Inline spacing={24}>
            <Radio.Label
              className={labelStyles}
              data-testid={`resultPage-departureArrivalFilter-departure${index}-radio`}
            >
              <Inline alignY="center" spacing={8}>
                <Radio
                  aria-label={t('ScreenReader.Result.ResultCard.TripBound.DepartureTime.Text')}
                  id={`departure-${index}`}
                  value="departure"
                />
                <span aria-hidden>{t('Result.SearchFilters.Departure.Label')}</span>
              </Inline>
            </Radio.Label>
            <Radio.Label
              className={labelStyles}
              data-testid={`resultPage-departureArrivalFilter-arrival${index}-radio`}
            >
              <Inline alignY="center" spacing={8}>
                <Radio
                  aria-label={t('ScreenReader.Result.ResultCard.TripBound.ArrivalTime.Text')}
                  id={`arrival-${index}`}
                  value="arrival"
                />
                <span aria-hidden>{t('Result.SearchFilters.Arrival.Label')}</span>
              </Inline>
            </Radio.Label>
          </Inline>
        </RadioGroup>
        <DoubleRangeSlider
          data-testid={`resultPage-departureArrivalFilter-${[selectedFilterType]}${index}-slider`}
          formatDisplayedValue={formatDateTimeValue(dateTime)}
          handleValues={[selectedValue.min, selectedValue.max]}
          handleValuesChange={handleChange}
          maxValue={endOfDay}
          maxValueLabel={maxValueLabelText}
          minValue={startOfDay}
          minValueLabel={minValueLabelText}
          step={halfHour}
        />
      </Stack>
    </Filter>
  );
};

export default DepartureArrivalTime;
